import cn from 'classnames';
import { Translate, t } from 'i18n';
import localeFlags from 'i18n/config/localeFlags';
import { FC } from 'react';

import { ILanguageInstance } from 'common/modules/ReactShared';
import { useAccessibleListNavigation } from 'students/views/shared/hooks';
import { CloseLightIcon } from '../../../../icons';
import { ILanguagesList } from './model';
import {
  SCloseButton,
  SFlagImg,
  SLanguagesListSearch,
  SLanguagesListSearchInput,
  SList,
  SListItem,
  SNoResultsMessage,
  SPicked,
  SSecondaryText
} from './styles';
import useLanguagesList from './useLanguagesList';

export const LanguagesList: FC<ILanguagesList> = ({
  languages,
  pickedLanIds,
  pickLang,
  hasFlag,
  withSearch,
  withAdditionalInfo
}) => {
  const {
    searchQuery,
    handleInputChange,
    clearSearch,
    filteredLanguages,
    currentTargetLanguage
  } = useLanguagesList(languages);

  const { listRef, listHandler, activeItemIndex } =
    useAccessibleListNavigation<ILanguageInstance>({
      items: filteredLanguages,
      onSelect: (lang) => pickLang(lang.id)
    });

  return (
    <div data-testid="students/components/LanguagesList">
      {withSearch ? (
        <SLanguagesListSearch>
          <SLanguagesListSearchInput
            data-testid="students/components/LanguagesList/SearchInput"
            value={searchQuery}
            onChange={handleInputChange}
            placeholder={t('frontend.search_placeholder')}
            dir="auto"
          />

          {searchQuery ? (
            <SCloseButton
              onClick={clearSearch}
              data-testid="students/components/LanguagesList/CloseButton"
            >
              <CloseLightIcon height="12px" width="12px" color="rgb(var(--ln-content))" />
            </SCloseButton>
          ) : null}
        </SLanguagesListSearch>
      ) : null}

      <SList ref={listRef} onKeyDown={listHandler}>
        {filteredLanguages.length ? (
          filteredLanguages.map((lan, idx) => {
            const flag = localeFlags.get(lan.code)?.flag;
            const onClick = () => pickLang(lan.id);
            const isPicked = !!pickedLanIds.find(
              (id: string) => id === `${lan.id}` || id === lan.code
            );
            return (
              <SListItem
                className={cn({ isPicked, isActive: activeItemIndex === idx })}
                data-testid="LanguagesList_item"
                onClick={onClick}
                key={lan.id}
              >
                {hasFlag && <SFlagImg src={flag} />}

                {lan.systemName.trim()}

                {withAdditionalInfo && lan.id === currentTargetLanguage?.languageId ? (
                  <SSecondaryText>
                    <Translate str="frontend.member.settings.pages.training_settings.set_as_i_learn" />
                  </SSecondaryText>
                ) : null}

                {isPicked && <SPicked data-testid="LanguagesList_lanActive" />}
              </SListItem>
            );
          })
        ) : (
          <SNoResultsMessage data-testid="students/components/LanguagesList/NotFoundMessage">
            <Translate str="frontend.course.no_results" />
          </SNoResultsMessage>
        )}
      </SList>
    </div>
  );
};
